import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  Alert,
  AlertIcon,
  Badge,
  Box,
  Button,
  Flex,
  HStack,
  IconButton,
  Kbd,
  Modal,
  ModalBody,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  SimpleGrid,
  Stack,
  Table,
  TableContainer,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tooltip,
  Tr,
  VStack,
  useBreakpointValue,
  useToast,
} from '@chakra-ui/react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Calendar,
  Eye,
  Mail,
  Pencil,
  Phone,
  Plus,
  Trash2,
  User2,
} from 'lucide-react';
import {
  deleteLead,
  fetchLeadsFromAPI,
  fetchLeadsWithUrl,
} from '../hooks/leads';
import { Lead, Organization } from '../utils/types';
import { getCurentOrgDataFromStorage } from '../services/organization';
import EmailButton from '../components/EmailButton';
import SalesWhatsAppButton from '../components/WhatsAppButton';
import {
  LeadCommandBar,
  LeadDashboardSkeleton,
  LeadEmptyState,
  LeadHero,
  LeadInterestBadge,
  LeadMetricStrip,
  LeadPrimaryButton,
  LeadSectionCard,
  LeadShell,
  LeadStageBadge,
  leadAccent,
} from './leads/components/leadsUi';

type SortKey = 'created_at' | 'first_name' | 'stage' | 'next_follow_up';
type SortDir = 'asc' | 'desc';

const formatDateTime = (iso?: string | null) =>
  iso
    ? new Date(iso).toLocaleString(undefined, {
        dateStyle: 'medium',
        timeStyle: 'short',
      })
    : 'N/A';

const useClientPagination = <T,>(rows: T[], pageSize: number) => {
  const [page, setPage] = useState(1);
  const pageCount = Math.max(1, Math.ceil(rows.length / pageSize));

  const current = useMemo(() => {
    const start = (page - 1) * pageSize;
    return rows.slice(start, start + pageSize);
  }, [rows, page, pageSize]);

  useEffect(() => {
    if (page > pageCount) {
      setPage(1);
    }
  }, [page, pageCount]);

  return { page, setPage, pageCount, current };
};

const LeadsDashboard: React.FC = () => {
  const navigate = useNavigate();
  const { orgId } = useParams();
  const toast = useToast();
  const pageSize = 10;

  const [leads, setLeads] = useState<Lead[]>([]);
  const [nextUrl, setNextUrl] = useState<string>('');
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [errorMsg, setErrorMsg] = useState<string | null>(null);
  const [org, setOrg] = useState<Organization>();
  const [leadsTotalCount, setLeadsTotalCount] = useState(0);

  const [stageFilter, setStageFilter] = useState<string>('');
  const [searchTerm, setSearchTerm] = useState<string>('');
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState('');
  const [sortKey, setSortKey] = useState<SortKey>('created_at');
  const [sortDir, setSortDir] = useState<SortDir>('desc');
  const [showDueOnly, setShowDueOnly] = useState<boolean>(false);
  const [selectedMetric, setSelectedMetric] = useState<string>('all');
  const [view, setView] = useState<'table' | 'cards'>('table');
  const [deleting, setDeleting] = useState<Lead | null>(null);

  const searchRef = useRef<HTMLInputElement>(null);
  const prefersCards = useBreakpointValue({ base: true, md: false });
  const showCardView = prefersCards || view === 'cards';
  const hasActiveFilters =
    Boolean(searchTerm.trim()) ||
    Boolean(stageFilter) ||
    showDueOnly ||
    selectedMetric !== 'all';

  useEffect(() => {
    if (prefersCards) {
      setView('cards');
    }
  }, [prefersCards]);

  useEffect(() => {
    const timeout = window.setTimeout(() => {
      setDebouncedSearchTerm(searchTerm.trim());
    }, 350);

    return () => window.clearTimeout(timeout);
  }, [searchTerm]);

  useEffect(() => {
    const onKey = (event: KeyboardEvent) => {
      if (event.key === '/' || event.key.toLowerCase() === 'f') {
        event.preventDefault();
        searchRef.current?.focus();
      }
      if (event.key.toLowerCase() === 'n') {
        event.preventDefault();
        navigate(`/organization/${orgId}/leads/new`);
      }
    };

    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [navigate, orgId]);

  const fetchOrg = async () => {
    const response = await getCurentOrgDataFromStorage();
    if (response.success && response.data && typeof response.data === 'object') {
      setOrg(response.data);
    }
  };

  const getLeadsData = async (id?: string, search?: string) => {
    if (!id) {
      return;
    }

    setLoading(true);
    setErrorMsg(null);
    try {
      const response = search
        ? await fetchLeadsWithUrl(id, null, { search, q: search })
        : await fetchLeadsFromAPI(id);
      if (response.success === true && response.data) {
        setLeads(response.data.results || []);
        setNextUrl(response.data.next || '');
        setLeadsTotalCount(response.data.count || 0);
      } else {
        setLeads([]);
        setErrorMsg('Failed to load leads.');
      }
    } catch (error) {
      setErrorMsg('Something went wrong while loading leads.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (orgId) {
      fetchOrg();
      getLeadsData(orgId, debouncedSearchTerm);
    }
  }, [orgId, debouncedSearchTerm]);

  const loadMoreFromServer = async () => {
    if (loadingMore || !nextUrl || !orgId) {
      return;
    }

    try {
      setLoadingMore(true);
      const response = await fetchLeadsWithUrl(orgId, nextUrl);

      if (response.success && response.data?.results) {
        setLeads((prev) => [...prev, ...response.data.results]);
        setNextUrl(response.data.next || '');
      }
    } catch (error) {
      toast({
        title: 'Could not load more leads',
        status: 'error',
        duration: 2500,
        isClosable: true,
      });
    } finally {
      setLoadingMore(false);
    }
  };

  const today = new Date().toISOString().split('T')[0];

  const filteredSorted = useMemo(() => {
    const filtered = leads.filter((lead) => {
      const matchesStage = stageFilter
        ? lead.stage?.toLowerCase() === stageFilter.toLowerCase()
        : true;

      const dueCheck = showDueOnly
        ? !!lead.next_follow_up && new Date(lead.next_follow_up) <= new Date()
        : true;

      const metricMatch =
        selectedMetric === 'all' ||
        (selectedMetric === 'new' && lead.stage === 'new') ||
        (selectedMetric === 'due' &&
          !!lead.next_follow_up &&
          new Date(lead.next_follow_up) <= new Date()) ||
        (selectedMetric === 'qualified' && lead.stage === 'qualified') ||
        (selectedMetric === 'hot' &&
          lead.level_of_interest_display === 'Highly Interested') ||
        (selectedMetric === 'won' && lead.stage === 'won');

      return matchesStage && dueCheck && metricMatch;
    });

    return filtered.slice().sort((a, b) => {
      const dir = sortDir === 'asc' ? 1 : -1;

      switch (sortKey) {
        case 'first_name':
          return (a.first_name || '').localeCompare(b.first_name || '') * dir;
        case 'stage':
          return (a.stage || '').localeCompare(b.stage || '') * dir;
        case 'next_follow_up':
          return (
            (new Date(a.next_follow_up || 0).getTime() -
              new Date(b.next_follow_up || 0).getTime()) * dir
          );
        case 'created_at':
        default:
          return (
            (new Date(a.created_at || 0).getTime() -
              new Date(b.created_at || 0).getTime()) * dir
          );
      }
    });
  }, [leads, stageFilter, showDueOnly, selectedMetric, sortKey, sortDir]);

  const { page, setPage, pageCount, current } = useClientPagination(
    filteredSorted,
    pageSize,
  );

  const stats = useMemo(() => {
    const dueCount = leads.filter(
      (lead) => lead.next_follow_up && new Date(lead.next_follow_up) <= new Date(),
    ).length;
    const newCount = leads.filter((lead) => lead.stage === 'new').length;
    const qualifiedCount = leads.filter((lead) => lead.stage === 'qualified').length;
    const hotCount = leads.filter(
      (lead) => lead.level_of_interest_display === 'Highly Interested',
    ).length;

    return [
      {
        label: 'Total leads',
        value: String(leadsTotalCount || leads.length),
        hint: 'All active leads',
        onClick: () => setSelectedMetric('all'),
        active: selectedMetric === 'all',
      },
      {
        label: 'New',
        value: String(newCount),
        hint: 'Need first response',
        onClick: () => setSelectedMetric('new'),
        active: selectedMetric === 'new',
      },
      {
        label: 'Due follow-ups',
        value: String(dueCount),
        hint: 'Need attention now',
        onClick: () => setSelectedMetric('due'),
        active: selectedMetric === 'due',
      },
      {
        label: 'Highly interested',
        value: String(hotCount),
        hint: 'Hot opportunities',
        onClick: () => setSelectedMetric('hot'),
        active: selectedMetric === 'hot',
      },
      {
        label: 'Qualified',
        value: String(qualifiedCount),
        hint: 'Sales-ready leads',
        onClick: () => setSelectedMetric('qualified'),
        active: selectedMetric === 'qualified',
      },
      {
        label: 'Won',
        value: String(leads.filter((lead) => lead.stage === 'won').length),
        hint: 'Converted successfully',
        onClick: () => setSelectedMetric('won'),
        active: selectedMetric === 'won',
      },
    ];
  }, [leads, leadsTotalCount, selectedMetric]);

  const clearFilters = () => {
    setSearchTerm('');
    setStageFilter('');
    setShowDueOnly(false);
    setSelectedMetric('all');
  };

  const confirmDelete = async () => {
    if (!deleting || !orgId) {
      return;
    }

    const response = await deleteLead(orgId, deleting.id);

    if (!response.success) {
      toast({
        title: 'Failed to delete lead',
        status: 'error',
        duration: 2200,
        isClosable: true,
      });
      return;
    }

    setLeads((prev) => prev.filter((lead) => lead.id !== deleting.id));
    setDeleting(null);
    toast({
      title: 'Lead deleted',
      status: 'success',
      duration: 2200,
      isClosable: true,
    });
  };

  const renderLeadActions = (lead: Lead) => (
    <HStack spacing={1}>
      {lead && org ? (
        <Tooltip label="Send WhatsApp Message">
          <Box>
            <SalesWhatsAppButton iconOnly={true} lead={lead} organization={org} />
          </Box>
        </Tooltip>
      ) : null}

      {lead.email && org ? (
        <Tooltip label="Send Mail">
          <Box>
            <EmailButton iconOnly={true} lead={lead} organization={org} />
          </Box>
        </Tooltip>
      ) : null}

      <Tooltip label="View">
        <IconButton
          aria-label="View lead"
          icon={<Eye size={16} />}
          size="sm"
          variant="ghost"
          onClick={() => navigate(`/organization/${orgId}/leads/${lead.id}`)}
        />
      </Tooltip>
      <Tooltip label="Edit">
        <IconButton
          aria-label="Edit lead"
          icon={<Pencil size={16} />}
          size="sm"
          variant="ghost"
          onClick={() => navigate(`/organization/${orgId}/leads/${lead.id}/edit`)}
        />
      </Tooltip>
      <Tooltip label="Delete">
        <IconButton
          aria-label="Delete lead"
          icon={<Trash2 size={16} />}
          size="sm"
          variant="ghost"
          colorScheme="red"
          onClick={() => setDeleting(lead)}
        />
      </Tooltip>
    </HStack>
  );

  const LeadMobileCard = ({ lead }: { lead: Lead }) => {
    const isDue =
      !!lead.next_follow_up && new Date(lead.next_follow_up).getTime() <= Date.now();
    return (
      <LeadSectionCard
        title={`${lead.title ?? ''} ${lead.first_name ?? ''} ${lead.second_name ?? ''}`.trim() || 'Unnamed lead'}
        description={lead.name_of_employee_assigned_to || lead.email || lead.phone || 'No contact details yet'}
        action={<LeadStageBadge stage={lead.stage} />}
      >
        <Stack spacing={4}>
          <HStack spacing={2} flexWrap="wrap">
            <LeadInterestBadge label={lead.level_of_interest_display} />
            {isDue ? (
              <Badge colorScheme="orange" borderRadius="full" px={3} py={1}>
                Follow-up due
              </Badge>
            ) : null}
            {lead.created_at?.startsWith(today) ? (
              <Badge colorScheme="blue" borderRadius="full" px={3} py={1}>
                Added today
              </Badge>
            ) : null}
          </HStack>

          <SimpleGrid columns={2} spacing={3}>
            <HStack align="flex-start">
              <Phone size={16} color="#718096" />
              <Text fontSize="sm" color="gray.600">
                {lead.phone || 'No phone'}
              </Text>
            </HStack>
            <HStack align="flex-start">
              <Mail size={16} color="#718096" />
              <Text fontSize="sm" color="gray.600" noOfLines={1}>
                {lead.email || 'No email'}
              </Text>
            </HStack>
            <HStack align="flex-start" gridColumn="span 2">
              <Calendar size={16} color="#718096" />
              <Text fontSize="sm" color="gray.600">
                Next follow-up: {formatDateTime(lead.next_follow_up)}
              </Text>
            </HStack>
          </SimpleGrid>

          {renderLeadActions(lead)}
        </Stack>
      </LeadSectionCard>
    );
  };

  return (
    <LeadShell>
      <LeadHero
        title="Manage your leads"
        description="Track new leads, follow-ups, interest levels, and stage movement from one place. Keep the team focused on the leads that need action next."
        primaryAction={
          <LeadPrimaryButton
            leftIcon={<Plus size={18} />}
            onClick={() => navigate(`/organization/${orgId}/leads/new`)}
          >
            Add Lead
          </LeadPrimaryButton>
        }
        secondaryAction={
          <Button borderRadius="16px" variant="outline" onClick={() => getLeadsData(orgId)}>
            Refresh
          </Button>
        }
      />

      <LeadMetricStrip items={stats} />

      <LeadCommandBar
        search={searchTerm}
        onSearchChange={setSearchTerm}
        inputRef={searchRef}
        stage={stageFilter}
        onStageChange={setStageFilter}
        dueOnly={showDueOnly}
        onDueOnlyChange={setShowDueOnly}
        sortKey={sortKey}
        onSortKeyChange={(value) => setSortKey(value as SortKey)}
        sortDir={sortDir}
        onSortDirToggle={() => setSortDir((prev) => (prev === 'asc' ? 'desc' : 'asc'))}
        view={view}
        onViewChange={setView}
        resultText={`${filteredSorted.length} of ${leadsTotalCount || leads.length} leads shown`}
      />

      {hasActiveFilters && (
        <HStack spacing={3} flexWrap="wrap" mb={5}>
          <Text fontSize="sm" color="gray.500">
            Active filters:
          </Text>
          {searchTerm ? <Badge borderRadius="full" px={3} py={1}>Search: {searchTerm}</Badge> : null}
          {stageFilter ? <Badge borderRadius="full" px={3} py={1}>Stage: {stageFilter}</Badge> : null}
          {showDueOnly ? <Badge borderRadius="full" px={3} py={1}>Due only</Badge> : null}
          {selectedMetric !== 'all' ? (
            <Badge borderRadius="full" px={3} py={1}>
              View: {selectedMetric}
            </Badge>
          ) : null}
          <Button size="sm" variant="ghost" onClick={clearFilters}>
            Clear all
          </Button>
        </HStack>
      )}

      <HStack justify="space-between" mb={4} flexWrap="wrap" gap={2}>
        <HStack spacing={3}>
          <Text fontSize="sm" color="gray.500">
            Quick keys:
          </Text>
          <Text fontSize="sm" color="gray.500">
            <Kbd>/</Kbd> search
          </Text>
          <Text fontSize="sm" color="gray.500">
            <Kbd>N</Kbd> add lead
          </Text>
        </HStack>
      </HStack>

      {loading ? <LeadDashboardSkeleton /> : null}

      {!loading && errorMsg ? (
        <Alert status="error" borderRadius="md" mb={4}>
          <AlertIcon />
          {errorMsg}
        </Alert>
      ) : null}

      {!loading && !errorMsg && filteredSorted.length === 0 ? (
        <LeadEmptyState
          title={searchTerm.trim() ? 'No leads match your search' : 'No leads found'}
          description={
            searchTerm.trim()
              ? `No leads matched "${searchTerm.trim()}". Try a different name, phone number, email, or clear the filters.`
              : 'Try clearing your filters or add a new lead to start building your pipeline.'
          }
          action={
            hasActiveFilters ? (
              <Button variant="outline" borderRadius="16px" onClick={clearFilters}>
                Clear filters
              </Button>
            ) : (
              <LeadPrimaryButton
                leftIcon={<Plus size={16} />}
                onClick={() => navigate(`/organization/${orgId}/leads/new`)}
              >
                Add Lead
              </LeadPrimaryButton>
            )
          }
        />
      ) : null}

      {!loading && !errorMsg && filteredSorted.length > 0 ? (
        <>
          {view === 'table' ? (
            <TableContainer
              display={{ base: 'none', md: 'block' }}
              bg="white"
              borderWidth="1px"
              borderColor="gray.200"
              borderRadius="28px"
              overflow="hidden"
              boxShadow="0 18px 44px rgba(15, 23, 42, 0.05)"
            >
              <Table variant="simple" size="md">
                <Thead bg="gray.50">
                  <Tr>
                    <Th>Lead</Th>
                    <Th>Stage</Th>
                    <Th>Interest</Th>
                    <Th>Next follow-up</Th>
                    <Th>Assigned to</Th>
                    <Th textAlign="right">Actions</Th>
                  </Tr>
                </Thead>
                <Tbody>
                  {current.map((lead) => {
                    const isDue =
                      !!lead.next_follow_up &&
                      new Date(lead.next_follow_up).getTime() <= Date.now();
                    return (
                      <Tr key={lead.id} _hover={{ bg: 'gray.50' }}>
                        <Td>
                          <Stack spacing={1}>
                            <HStack spacing={3}>
                              <Box
                                w="40px"
                                h="40px"
                                borderRadius="full"
                                bg="green.50"
                                color={leadAccent}
                                display="flex"
                                alignItems="center"
                                justifyContent="center"
                              >
                                <User2 size={16} />
                              </Box>
                              <Box>
                                <Text fontWeight="700" color="gray.900">
                                  {`${lead.title ?? ''} ${lead.first_name ?? ''} ${lead.second_name ?? ''}`.trim() ||
                                    'Unnamed lead'}
                                </Text>
                                <Text fontSize="sm" color="gray.500">
                                  {lead.email || lead.phone || 'No contact details'}
                                </Text>
                              </Box>
                            </HStack>
                          </Stack>
                        </Td>
                        <Td>
                          <LeadStageBadge stage={lead.stage} />
                        </Td>
                        <Td>
                          <LeadInterestBadge label={lead.level_of_interest_display} />
                        </Td>
                        <Td>
                          <Stack spacing={1}>
                            <Text color={isDue ? 'orange.600' : 'gray.700'} fontWeight={isDue ? '700' : '500'}>
                              {formatDateTime(lead.next_follow_up)}
                            </Text>
                            {isDue ? (
                              <Text fontSize="xs" color="orange.500">
                                Needs follow-up
                              </Text>
                            ) : null}
                          </Stack>
                        </Td>
                        <Td>
                          <Text color="gray.600">
                            {lead.name_of_employee_assigned_to || 'Unassigned'}
                          </Text>
                        </Td>
                        <Td>
                          <Flex justify="flex-end">{renderLeadActions(lead)}</Flex>
                        </Td>
                      </Tr>
                    );
                  })}
                </Tbody>
              </Table>
            </TableContainer>
          ) : null}

          {showCardView ? (
            <Stack display={{ base: 'flex', md: view === 'cards' ? 'flex' : 'none' }} spacing={4}>
              {current.map((lead) => (
                <LeadMobileCard key={lead.id} lead={lead} />
              ))}
            </Stack>
          ) : null}

          <HStack mt={5} justify="space-between" flexWrap="wrap" gap={3} mb={12}>
            <Text fontSize="sm" color="gray.600">
              Showing {(page - 1) * pageSize + 1}-
              {Math.min(page * pageSize, filteredSorted.length)} of {leadsTotalCount || filteredSorted.length}
            </Text>
            <HStack spacing={2}>
              <Button
                size="sm"
                variant="outline"
                onClick={() => setPage((p) => Math.max(1, p - 1))}
                isDisabled={page === 1}
              >
                Prev
              </Button>
              <Badge px={3} py={1} borderRadius="md">
                Page {page} / {pageCount}
              </Badge>
              <Button
                size="sm"
                variant="outline"
                onClick={() => setPage((p) => Math.min(pageCount, p + 1))}
                isDisabled={page === pageCount}
              >
                Next
              </Button>
              {nextUrl ? (
                <Button size="sm" variant="ghost" onClick={loadMoreFromServer} isLoading={loadingMore}>
                  Load more
                </Button>
              ) : null}
            </HStack>
          </HStack>
        </>
      ) : null}

      <Button
        display={{ base: 'inline-flex', md: 'none' }}
        position="fixed"
        bottom={5}
        right={5}
        zIndex={20}
        bg={leadAccent}
        color="white"
        borderRadius="full"
        w="60px"
        h="60px"
        p={0}
        boxShadow="0 16px 30px rgba(0, 100, 0, 0.22)"
        aria-label="Add lead"
        onClick={() => navigate(`/organization/${orgId}/leads/new`)}
      >
        <Plus size={22} />
      </Button>

      <Modal isOpen={Boolean(deleting)} onClose={() => setDeleting(null)} isCentered>
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>Delete lead?</ModalHeader>
          <ModalBody>
            <Text>
              This action cannot be undone. You are about to delete{' '}
              <Badge colorScheme="red">
                {`${deleting?.title ?? ''} ${deleting?.first_name ?? ''} ${deleting?.second_name ?? ''}`.trim() ||
                  'this lead'}
              </Badge>
              .
            </Text>
          </ModalBody>
          <ModalFooter>
            <Button variant="ghost" mr={3} onClick={() => setDeleting(null)}>
              Cancel
            </Button>
            <Button colorScheme="red" onClick={confirmDelete} leftIcon={<Trash2 size={16} />}>
              Delete
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </LeadShell>
  );
};

export default LeadsDashboard;
